/**
 * Configuración de la vista de lista "Modificadores globales".
 */

import { ProductIcon, PlusIcon } from '@/lib/polaris-icons';
import type { ListViewConfig } from '@/types/listView.types';
import { getStatusBadgeTone } from '@/types/listView.types';
import { ROUTES } from '@/config/routes.config';

export interface ModifierListItem {
  id: string;
  slug: string;
  name: string;
  price_adjustment: number;
  tenant_id: string;
  created_at: string;
  /** Opcional: si la tabla tiene is_active en el futuro */
  is_active?: boolean;
}

export type NavigateFn = (path: string) => void;

export interface ModifiersListConfigOptions {
  /** Si se define, "Agregar modificador" abre este callback (modal). Si no, navega a página nueva. */
  onAddModifier?: () => void;
}

export function getModifiersListConfig(
  toUrl: NavigateFn,
  options?: ModifiersListConfigOptions
): ListViewConfig<ModifierListItem> {
  const addAction = options?.onAddModifier ?? (() => toUrl(ROUTES.PRODUCTS_MODIFIERS_NEW));
  return {
    title: 'Modificadores Globales',
    titleIcon: ProductIcon,
    onBack: () => toUrl(ROUTES.PRODUCTS),
    backAccessibilityLabel: 'Volver a Productos',
    primaryAction: {
      onAction: addAction,
      icon: PlusIcon,
    },
    resourceName: { singular: 'modificador', plural: 'modificadores' },
    columns: [
      {
        key: 'name',
        title: 'Nombre',
        type: 'text',
        fontWeight: 'medium',
      },
      {
        key: 'price_adjustment',
        title: 'Ajuste de Precio',
        type: 'badge',
        format: (v) =>
          `${Number(v) >= 0 ? '+' : ''} S/ ${Number(v).toFixed(2)}`,
        toneFromValue: (v) => (Number(v) >= 0 ? 'success' : 'attention'),
      },
      {
        key: 'is_active',
        title: 'Estado',
        type: 'badge',
        map: { true: 'Activo', false: 'Inactivo' },
        format: (v) => (v === false ? 'Inactivo' : 'Activo'),
        toneFromValue: getStatusBadgeTone,
      },
      {
        key: 'actions',
        title: 'Acciones',
        type: 'actions',
        editRoute: ROUTES.PRODUCTS_MODIFIERS_EDIT,
        editLabel: 'Editar',
        deleteLabel: 'Eliminar',
        delete: true,
      },
    ],
    emptyState: {
      heading: 'No hay modificadores creados',
      description: 'Crea modificadores para personalizar tus productos.',
      image: 'https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png',
      action: {
        content: 'Agregar modificador',
        onAction: addAction,
      },
    },
    bulkActions: [
      { content: 'Exportar datos', actionKey: 'export' },
      // "Eliminar" lo inyecta la plantilla vía prop `bulkDelete`.
    ],
    idKey: 'id',
  };
}
